import * as React from 'react'

import { menuScreenElementIds } from '~shared/config/menuScreenElementIds'

import { useCategoryElements } from '../hooks/useCategoryElements'
import { useCategoryObserver } from '../hooks/useCategoryObserver'

type TOptions = {
  /**
   * known height of a sticky content
   */
  stuckContentHeight: number
  /**
   * used as a dependency to recalculate the intersection observer settings
   */
  dependencyData: any
}

const { productListContainerId } = menuScreenElementIds

/**
 * The hook is responsible for watching product categories rendered inside of
 * the container with the given id `productListContainerId`.
 * It returns currently selected category id, which is either
 * the category intersecting the viewport boundaries (specified by the observer options)
 * OR the category selected using `onSelection` handler.
 */
export const useProductListSelection = ({ stuckContentHeight, dependencyData }: TOptions) => {
  const [reconnectDependency, setReconnectDependency] = React.useState(0)

  const [selectedId, setSelectedId] = React.useState<Nullable<ID>>(null)

  const refListElements = useCategoryElements({
    dependencyData,
    containerId: productListContainerId,
  })

  const refObserver = useCategoryObserver({
    reconnectDependency,
    refListElements,
    dependencyData,
    onCategoryScrolledTo: setSelectedId,
    stuckContentHeight,
  })

  const scrollToCategory = React.useCallback(
    (selectedId: ID) => {
      const categoryElement = refListElements.current.find((element) => element.id === selectedId)
      const container = categoryElement?.offsetParent as HTMLElement | undefined

      const containerOffset = container?.offsetTop ?? 0
      const categoryOffset = categoryElement?.offsetTop ?? 0
      const scrollPosition = containerOffset + categoryOffset - stuckContentHeight

      window.scrollTo({ top: scrollPosition, behavior: `smooth` })
    },
    [refListElements, stuckContentHeight],
  )

  // scroll to the selected category
  // turn off the observer to prevent automatic selection while scrolling
  // turn on the observer after a scroll event is finished
  // if no category should be selected (meaning no `selectedId` is passed),
  // the callback just turns off the observer for a while and then turns it on again
  const scrollToCategoryWithoutObserver = React.useCallback(
    (selectedId?: ID) => {
      // disconnect the observer to prevent automatic selection while scrolling
      refObserver.current?.disconnect()

      if (selectedId) {
        setSelectedId(selectedId)
        scrollToCategory(selectedId)
      }

      // reconnect the observer after a scroll event is finished
      // by increasing the count, that is used as a dependency for the observer
      setTimeout(() => {
        setReconnectDependency((prev) => prev + 1)
      }, 700)
    },
    [refObserver, scrollToCategory],
  )

  return {
    selectedId,
    scrollToCategoryWithoutObserver,
  }
}
